import React from 'react';
import { Check, X } from 'lucide-react';

const MemoryConsentModal = ({
  isOpen,
  isWorking,
  step = 'intro',
  progressText,
  error,
  nickname = '',
  occupation = '',
  onEnable,
  onDisable,
  onNicknameChange,
  onOccupationChange,
  onSaveProfile,
  onSkipProfile,
}) => {
  if (!isOpen) return null;

  const isProfileStep = step === 'profile';
  const handleClose = isProfileStep ? onSkipProfile : onDisable;

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/45 px-4 backdrop-blur-[3px]">
      <div className="relative w-full max-w-[720px] overflow-hidden rounded-[34px] border theme-border theme-app-bg shadow-2xl">
        <div className="relative h-[245px] overflow-hidden sm:h-[285px]">
          <div
            className="absolute inset-0"
            style={{
              background:
                'linear-gradient(145deg, rgba(255, 246, 238, 0.78) 0%, rgba(244, 237, 232, 0.72) 34%, rgba(255, 255, 255, 0.92) 78%)',
            }}
          />
          <div
            className="absolute inset-0 opacity-75"
            style={{
              background:
                'linear-gradient(115deg, rgba(255,255,255,0.78) 0%, transparent 35%), linear-gradient(20deg, transparent 18%, rgba(255,255,255,0.54) 48%, transparent 74%)',
            }}
          />
          <div
            className="absolute -left-20 top-8 h-52 w-[120%] -rotate-12 opacity-45 blur-2xl"
            style={{
              background:
                'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.82) 28%, rgba(230, 214, 201, 0.46) 52%, rgba(255,255,255,0.58) 78%, transparent 100%)',
            }}
          />
          <button
            type="button"
            className="absolute right-6 top-6 flex h-8 w-8 items-center justify-center rounded-full bg-white/45 text-gray-600 backdrop-blur-md transition hover:bg-white/70 disabled:opacity-60"
            onClick={handleClose}
            disabled={isWorking}
            title="Close"
          >
            <X size={17} />
          </button>

          <div className="absolute inset-0">
            <div className="absolute left-[10%] top-[12%] rounded-full bg-white/20 px-5 py-2 text-sm text-gray-500/20 shadow-sm">
              ~~~ ~~
            </div>
            <div className="absolute right-[17%] top-[13%] rounded-full bg-white/24 px-5 py-2 text-sm text-gray-500/20 shadow-sm">
              ~~~ ~~
            </div>
            <div className="absolute left-[58%] top-[23%] rounded-full bg-white/24 px-6 py-2 text-sm text-gray-500/20 shadow-sm">
              ~~~~~ ~~
            </div>
            <div className="absolute left-[4%] top-[48%] rounded-full bg-white/18 px-5 py-2 text-sm text-gray-500/20 shadow-sm">
              ~~~~~~~
            </div>
            <div className="absolute bottom-[18%] right-[7%] rounded-full bg-white/24 px-6 py-2 text-sm text-gray-500/20 shadow-sm">
              ~~~~~~~ ~~
            </div>
            <div className="absolute bottom-[22%] left-[42%] rounded-full bg-white/22 px-5 py-2 text-sm text-gray-500/20 shadow-sm">
              ~~~~~ ~~
            </div>
          </div>

          <div className="absolute left-6 top-16 flex max-w-[340px] flex-col gap-3 sm:left-10 sm:max-w-[390px]">
            <div className="rounded-2xl bg-white/78 px-4 py-3 text-[15px] font-medium leading-snug text-gray-900 shadow-sm backdrop-blur-md sm:px-5 sm:text-[17px]">
              Can you remind me what we worked on recently?
            </div>
            <div className="rounded-2xl bg-white/82 px-4 py-3 text-[15px] font-medium leading-snug text-gray-900 shadow-sm backdrop-blur-md sm:px-5 sm:text-[17px]">
              I do not want to explain everything again.
            </div>
          </div>
        </div>

        <div className="relative theme-app-bg px-6 pb-7 pt-7 sm:px-10 sm:pb-9 sm:pt-8">
          <h2 className="text-[29px] font-semibold leading-tight tracking-normal theme-text-primary sm:text-[34px]">
            {isProfileStep ? 'Help Arrow remember the basics' : 'Meet Arrow’s new memory'}
          </h2>
          <p className="mt-4 max-w-[610px] text-[16px] leading-7 theme-text-secondary sm:text-[17px]">
            {isProfileStep
              ? 'While your chats are indexing in the background, add a couple details Arrow should keep in mind.'
              : 'Choose whether Arrow can save useful details and reference your past chats. If you enable memory, your history will be processed on our servers, even when the app is closed. You can turn memory off or delete it in Settings.'}
          </p>

          {progressText && (
            <div className="mt-4 flex items-center gap-2 rounded-2xl border theme-border bg-[var(--color-button-primary)]/8 px-4 py-3 text-sm theme-text-secondary">
              <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-[var(--color-button-primary)] text-white">
                <Check size={13} />
              </span>
              <span>{progressText}</span>
            </div>
          )}

          {isProfileStep && (
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <label className="block">
                <span className="mb-2 block text-sm font-medium theme-text-primary">What should Arrow call you?</span>
                <input
                  value={nickname}
                  onChange={(event) => onNicknameChange?.(event.target.value)}
                  disabled={isWorking}
                  placeholder="Nickname"
                  className="h-12 w-full rounded-xl border theme-border theme-card-bg px-4 text-base theme-text-primary placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[var(--color-button-primary)]/30 disabled:opacity-60"
                />
              </label>
              <label className="block">
                <span className="mb-2 block text-sm font-medium theme-text-primary">What do you do?</span>
                <input
                  value={occupation}
                  onChange={(event) => onOccupationChange?.(event.target.value)}
                  disabled={isWorking}
                  placeholder="Occupation or role"
                  className="h-12 w-full rounded-xl border theme-border theme-card-bg px-4 text-base theme-text-primary placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[var(--color-button-primary)]/30 disabled:opacity-60"
                />
              </label>
            </div>
          )}

          {error && (
            <div className="mt-4 rounded-2xl border border-red-200 bg-red-50/80 p-3 text-sm text-red-600 dark:border-red-900 dark:bg-red-950/30 dark:text-red-400">
              {error}
            </div>
          )}

          <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-end">
            <button
              type="button"
              className="h-[52px] rounded-xl border-2 theme-border bg-transparent px-6 py-3 text-base font-semibold theme-text-primary transition hover:bg-black/5 disabled:opacity-60 dark:hover:bg-white/10 sm:h-14 sm:px-7"
              onClick={isProfileStep ? onSkipProfile : onDisable}
              disabled={isWorking}
            >
              {isProfileStep ? 'Skip for now' : "Don't use memory"}
            </button>
            <button
              type="button"
              className="h-[52px] rounded-xl bg-black px-6 py-3 text-base font-semibold text-white shadow-lg shadow-black/10 transition hover:bg-black/90 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-white dark:text-black dark:hover:bg-white/90 sm:h-14 sm:px-8"
              onClick={isProfileStep ? onSaveProfile : onEnable}
              disabled={isWorking}
            >
              {isWorking
                ? isProfileStep
                  ? 'Saving...'
                  : 'Turning on...'
                : isProfileStep
                  ? 'Save memory'
                  : 'Use memory'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MemoryConsentModal;
